import { ProForm } from "@ant-design/pro-components";
import { useIntl } from "@umijs/max";
import { Button, Form, TreeSelect } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { listOperatorTree, type MerchantItem } from "@/services/merchant";
import AgentLevelTag from "../AgentLevelTag";

interface MerchantSelectProps {
  /** 是否支持多选 */
  multiple?: boolean;
  /** 是否包含父级 (多选模式下：当点击父级时选中所有子级并提交父级ID；若子级未全选，父级不选中且不提交父级ID) */
  includeParent?: boolean;
  /** 是否只允许选择商户节点，不允许选择代理节点 */
  merchantOnly?: boolean;
  /** 表单字段名 */
  name?: string;
  /** 标签文本 */
  label?: string;
  /** 占位提示语 */
  placeholder?: string;
  /** fieldProps 透传 */
  fieldProps?: any;
  /** 其他属性透传给 ProFormTreeSelect */
  [key: string]: any;
}

/**
 * 渲染树节点标题
 */
const TreeNodeTitle = ({
  name,
  level,
  isEmptyAgent,
}: {
  name: string;
  level: number;
  isEmptyAgent: boolean;
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      overflow: "hidden",
      opacity: isEmptyAgent ? 0.5 : 1,
    }}
  >
    <span
      style={{
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        marginRight: "8px",
      }}
      title={name}
    >
      {name}
    </span>
    <AgentLevelTag
      level={level}
      style={{ marginRight: 0, fontSize: "10px", lineHeight: "16px" }}
    />
  </div>
);

/**
 * 格式化树形数据
 */
const formatTreeData = (
  data: MerchantItem[],
  intl: any,
  merchantOnly = false,
): any[] => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => {
    const isMerchant = item.agentLevel === 4;
    const children =
      item.children && item.children.length > 0
        ? formatTreeData(item.children, intl, merchantOnly)
        : undefined;

    // 如果是代理且没有子节点，则禁用复选框（不可选），但依然展示
    const isEmptyAgent = !isMerchant && (!children || children.length === 0);
    const canSelect = merchantOnly ? isMerchant : !isEmptyAgent;

    return {
      title: (
        <TreeNodeTitle
          name={item.operatorName}
          level={item.agentLevel}
          isEmptyAgent={isEmptyAgent}
        />
      ),
      // searchValue 用于搜索过滤，因为 title 变成了 ReactNode
      label: item.operatorName, // 选中后在 input 内显示的文本（不带 tag）
      searchValue: item.operatorName,
      value: item.appid,
      key: item.appid, // 显式设置 key
      selectable: canSelect,
      disableCheckbox: !canSelect,
      children,
    };
  });
};

/**
 * 收集树中所有可选节点 value
 * includeParent=false 时仅全选叶子商户
 * includeParent=true 时会同时包含可选父级
 */
const collectSelectableValues = (
  data: any[],
  includeParent: boolean,
): string[] => {
  if (!data || !Array.isArray(data)) return [];

  const values: string[] = [];

  const traverse = (nodes: any[]) => {
    nodes.forEach((node) => {
      const hasChildren =
        Array.isArray(node.children) && node.children.length > 0;
      const isSelectable = node.selectable !== false && !node.disableCheckbox;

      if (isSelectable && (includeParent || !hasChildren)) {
        values.push(node.value);
      }

      if (hasChildren) {
        traverse(node.children);
      }
    });
  };

  traverse(data);

  return Array.from(new Set(values));
};

/**
 * 独立封装的商户/代理选择组件 (树形结构)
 * 数据来源: /api/v1/sys/listOperatorTree
 */
const MerchantSelect = ({
  multiple = false,
  includeParent = false,
  merchantOnly = false,
  name = "appids",
  label,
  placeholder,
  fieldProps,
  ...rest
}: MerchantSelectProps) => {
  const intl = useIntl();
  const form = ProForm.useFormInstance();
  const [treeData, setTreeData] = useState<any[]>([]);

  const {
    popupRender: externalPopupRender,
    onChange: externalOnChange,
    ...restFieldProps
  } = fieldProps || {};

  const allSelectableValues = useMemo(
    () => collectSelectableValues(treeData, includeParent),
    [treeData, includeParent],
  );

  const updateValue = useCallback(
    (nextValue: string[] | undefined) => {
      form?.setFieldValue?.(name, nextValue);
      externalOnChange?.(nextValue);
    },
    [externalOnChange, form, name],
  );

  const handleSelectAll = useCallback(() => {
    updateValue(
      allSelectableValues.length > 0 ? allSelectableValues : undefined,
    );
  }, [allSelectableValues, updateValue]);

  const handleClearAll = useCallback(() => {
    updateValue(undefined);
  }, [updateValue]);

  const mergedFieldProps = useMemo(
    () => ({
      treeData,
      placeholder:
        placeholder ||
        intl.formatMessage({ id: "pages.common.merchant.placeholder" }),
      multiple,
      treeCheckable: multiple,
      // 如果开启了 includeParent，则使用 SHOW_ALL (包含选中的父级和子级)
      // 否则保持原有逻辑使用 SHOW_CHILD (仅包含选中的子级)
      showCheckedStrategy: includeParent
        ? TreeSelect.SHOW_ALL
        : TreeSelect.SHOW_CHILD,
      treeDefaultExpandAll: false,
      allowClear: true,
      showSearch: true,
      treeNodeFilterProp: "searchValue",
      treeNodeLabelProp: "label", // 关键：指定选中后在 input 内展示的字段，避免展示 title 中的 Tag
      ...restFieldProps,
      popupRender: (originNode: React.ReactNode, props: any) => {
        const content = (
          <>
            {originNode}
            {multiple && (
              <div
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  padding: "8px 12px",
                  borderTop: "1px solid #f0f0f0",
                  background: "#fff",
                }}
              >
                <Button size="small" type="link" onClick={handleSelectAll}>
                  选择全部
                </Button>
                <Button size="small" type="link" onClick={handleClearAll}>
                  清空
                </Button>
              </div>
            )}
          </>
        );

        return externalPopupRender
          ? externalPopupRender(content, props)
          : content;
      },
    }),
    [
      placeholder,
      treeData,
      multiple,
      includeParent,
      restFieldProps,
      intl,
      handleSelectAll,
      handleClearAll,
      externalPopupRender,
    ],
  );

  const [loading, setLoading] = useState(false);

  const handleRequest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listOperatorTree({});
      // 兼容可能直接返回数组的情况
      const list = Array.isArray(res) ? res : res?.list || [];
      const formattedTreeData = formatTreeData(list, intl, merchantOnly);
      setTreeData(formattedTreeData);
    } catch (error) {
      console.error("Failed to fetch operator tree:", error);
      setTreeData([]);
    } finally {
      setLoading(false);
    }
  }, [intl, merchantOnly]);

  useEffect(() => {
    void handleRequest();
  }, [handleRequest]);

  return (
    <Form.Item
      {...rest}
      name={name}
      label={
        label || intl.formatMessage({ id: "pages.common.merchant.select" })
      }
    >
      <TreeSelect {...mergedFieldProps} loading={loading} />
    </Form.Item>
  );
};

export default MerchantSelect;
